import { useMutation } from '@apollo/client'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { Button, Fields, FormControl, MenuItem, Row, Stack, Text, useAlert } from '../../components'
import { validations } from '../../components/form'
import { mutations } from '../../graphql'
import { parseAndSetFormErrors } from '../../utils'

export function ChangeEmailForm({ currentEmail, availableEmails }) {
  const [emailUpdated, setEmailUpdated] = useState(false)
  const { register, handleSubmit, formState, setError, setValue, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  const { isSubmitting, errors = {} } = formState
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [updateEmail] = useMutation(mutations.user.updateEmail, {
    onCompleted: () => {
      setEmailUpdated(true)
      setAlert({ message: 'Email successfully updated', severity: 'success' })
    },
    onError: (newErrors) => {
      parseAndSetFormErrors(newErrors, setError)
      setAlert({ message: `Email update failed: ${newErrors.message}`, severity: 'error' })
    },
  })

  const onSubmit = async (data) => {
    updateEmail({
      variables: { data },
    })
  }

  const restartForm = () => {
    setEmailUpdated(false)
  }

  return (
    <form data-testid="change-primary-email-form" onSubmit={handleSubmit(onSubmit)}>
      {emailUpdated ? (
        <Stack>
          <Text.H6>Email Successfully Updated</Text.H6>
          <Text.Body data-testid="dialogContent">
            Your email is updated and a confirmation has been sent. You&apos;ll need to use this address to log in.
          </Text.Body>
          <Row justifyContent="flex-end" alignItems="center" m={4}>
            <Button data-testid="finalize-email-change-button" color="info" onClick={restartForm}>
              OK
            </Button>
          </Row>
        </Stack>
      ) : (
        <Stack>
          <Text.H6 data-testid="dialogTitle">Change Email Address</Text.H6>
          <Stack mx={2}>
            <Text.Body data-testid="dialogContent">
              This will change the email address that is used to log into this Giving Fund.
            </Text.Body>
            <FormControl fullWidth>
              <Fields.Text
                select
                defaultValue={currentEmail}
                onChange={(e) => {
                  setValue('newEmail', e.target.value, { shouldValidate: true })
                }}
                label="Change Log In Email To *"
                labelId="newEmailLabel"
                error={!!errors.newEmail}
                {...register('newEmail', { ...validations.required })}
              >
                {availableEmails.map((availableEmail) => (
                  <MenuItem key={availableEmail.id} value={availableEmail.email}>
                    {availableEmail.email}
                  </MenuItem>
                ))}
              </Fields.Text>
            </FormControl>
          </Stack>
          <Row justifyContent="flex-end" alignItems="center" m={4}>
            <Button color="info" onClick={() => reset()}>
              Reset
            </Button>
            <Button disabled={isSubmitting} type="submit" data-testid="change-email-button">
              Change Email
            </Button>
          </Row>
          <Alert {...alertProps} />
        </Stack>
      )}
    </form>
  )
}
